import { useState } from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Combobox } from '@opendoor/atoms/combobox';
import { Field } from '@opendoor/atoms/field';
import { Link } from '@opendoor/atoms/link';
import { Text } from '@opendoor/atoms/text';
import type { ILpComponentOpenPositions } from 'declarations/contentful';

import { LandingPageSectionContainer } from './shared/Container';

// GROWTH-1355 POC (Path B — full API integration). Throwaway branch; do not ship.
// v1 endpoint returns all jobs in a single response with clean department labels;
// v2 carries the full HTML description + employmentType + companyName for JD pages.
type Board = 'opendoor' | 'osn';
const BOARDS: Board[] = ['opendoor', 'osn'];
const isBoard = (s: string): s is Board => (BOARDS as readonly string[]).includes(s);
const BOARD_LABEL: Record<Board, string> = {
  opendoor: 'Opendoor',
  osn: 'OS National',
};
const ripplingListUrl = (board: Board) =>
  `https://api.rippling.com/platform/api/ats/v1/board/${board}/jobs`;
const ripplingDetailUrl = (board: Board, uuid: string) =>
  `https://ats.rippling.com/api/v2/board/${board}/jobs/${uuid}`;
const ripplingApplyUrl = (board: Board, uuid: string) =>
  `https://ats.rippling.com/${board}/jobs/${uuid}/apply`;

interface RipplingV1Job {
  uuid: string;
  name: string;
  url: string;
  department: { id: string; label: string };
  workLocation: { id: string; label: string };
}

// One row per (uuid, name, dept) collapsed across the multiple location rows
// Rippling emits for multi-location reqs.
interface ListingJob {
  board: Board;
  uuid: string;
  name: string;
  department: { id: string; label: string };
  locationLabels: string[];
}

interface RipplingV2Workplace {
  name?: string;
  country?: string;
  city?: string;
  state?: string;
  workplaceType?: string;
}

// v2 is inconsistent: some jobs return workLocations as objects, others as plain strings.
type RipplingV2WorkLocation = string | RipplingV2Workplace;

interface RipplingV2Job {
  board: Board;
  uuid: string;
  name: string;
  description: { company: string; role: string };
  department: { name: string; base_department: string; department_tree: string[] };
  employmentType?: { id: string; label: string };
  createdOn: string;
  companyName: string;
  workLocations?: RipplingV2WorkLocation[];
  url: string;
}

const locName = (loc: RipplingV2WorkLocation): string =>
  typeof loc === 'string' ? loc : (loc.name ?? '');

const locWorkplace = (loc: RipplingV2WorkLocation): RipplingV2Workplace =>
  typeof loc === 'string' ? { name: loc } : loc;

const PROD_HOST = 'https://www.opendoor.com';

const slugify = (s: string): string =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80)
    .replace(/-+$/g, '');

const jobPath = (job: { board: Board; uuid: string; name: string }): string => {
  const tail = `${slugify(job.name)}-${job.uuid}`;
  return job.board === 'opendoor'
    ? `/careers/open-positions/jobs/${tail}`
    : `/careers/open-positions/jobs/${job.board}/${tail}`;
};

const jobAbsoluteUrl = (job: { board: Board; uuid: string; name: string }): string =>
  `${PROD_HOST}${jobPath(job)}`;

const UUID_AT_END_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface JobRef {
  board: Board;
  uuid: string;
}

// Pull (board, uuid) from a URL like:
//   /careers/open-positions/jobs/<slug>-<uuid>          → opendoor (implicit)
//   /careers/open-positions/jobs/osn/<slug>-<uuid>      → osn (explicit)
//   /careers/open-positions/jobs?req_id=<uuid>          → opendoor (legacy)
const extractJobRef = (url: string): JobRef | null => {
  try {
    const u = new URL(url, PROD_HOST);
    const segments = u.pathname.split('/').filter(Boolean);
    // Find the "jobs" segment, then inspect what follows.
    const jobsIdx = segments.indexOf('jobs');
    if (jobsIdx >= 0) {
      const rest = segments.slice(jobsIdx + 1);
      let board: Board;
      let tail: string;
      if (rest.length === 1) {
        // Implicit opendoor: /jobs/<slug>-<uuid>
        board = 'opendoor';
        tail = rest[0];
      } else if (rest.length === 2 && isBoard(rest[0])) {
        // Explicit board: /jobs/<board>/<slug>-<uuid>
        board = rest[0];
        tail = rest[1];
      } else {
        return null;
      }
      const tailMatch = tail.match(UUID_AT_END_RE);
      if (tailMatch) return { board, uuid: tailMatch[0].toLowerCase() };
    }
    // Backwards compat: ?req_id=<uuid> always referred to the Opendoor board.
    const reqId = u.searchParams.get('req_id');
    if (reqId && UUID_AT_END_RE.test(reqId)) {
      return { board: 'opendoor', uuid: reqId.toLowerCase() };
    }
  } catch {
    return null;
  }
  return null;
};

interface ListingResolved {
  departmentFilters: Array<{ label: string; value: string }>;
  officeFilters: Array<{ label: string; value: string }>;
  jobsByDepartment: Array<{ label: string; jobs: ListingJob[] }>;
}

interface DetailResolved {
  job: RipplingV2Job | null;
  jobPostingSchema: object | null;
  seoOverride: SeoOverride | null;
}

interface SeoOverride {
  title: string;
  canonicalUrl: string;
  metaDescription: string;
  ogTitle: string;
  ogUrl: string;
  ogDescription: string;
}

type Resolved = Partial<ListingResolved & DetailResolved>;

type DescriptionBlock =
  | { kind: 'heading'; text: string }
  | { kind: 'paragraph'; text: string }
  | { kind: 'list'; items: string[] };

const HEADING_RE = /^[A-Z][\w\s&,/'-]{0,60}$/;

// Strip styling tags, decode entities, collapse whitespace within a paragraph or list item.
const stripInline = (s: string): string =>
  s
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/?(?:span|strong|em|b|i|u)[^>]*>/gi, '')
    .replace(/<a[^>]*>([\s\S]*?)<\/a>/gi, '$1')
    .replace(/<[^>]+>/g, '')
    .replace(/[ \t]+/g, ' ')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n[ \t]+/g, '\n')
    .trim();

// Promote a heading-eligible single line ("**Section**", "Section:", "Section") into a heading
// block, otherwise emit a paragraph. Used for paragraphs that may carry markdown-in-text intent.
const lineToBlock = (line: string): DescriptionBlock => {
  const cleaned = line
    .replace(/^\*\*\s*/, '')
    .replace(/\s*\*\*\s*$/, '')
    .replace(/:\s*$/, '')
    .replace(/\*\*/g, '')
    .trim();
  if (cleaned.length > 0 && cleaned.length <= 60 && HEADING_RE.test(cleaned)) {
    return { kind: 'heading', text: cleaned };
  }
  return { kind: 'paragraph', text: line.replace(/\*\*/g, '').trim() };
};

const segmentToBlocks = (segment: string): DescriptionBlock[] => {
  const lines = segment
    .split(/\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length === 0) return [];
  if (lines.length > 1 && lines.every((l) => /^[*•-]\s+/.test(l))) {
    return [{ kind: 'list', items: lines.map((l) => l.replace(/^[*•-]\s+/, '')) }];
  }
  if (lines.length === 1) return [lineToBlock(lines[0])];
  return [{ kind: 'paragraph', text: lines.join(' ').replace(/\*\*/g, '').trim() }];
};

// Rippling content varies wildly: some roles use real <h1-3>/<ul>/<li>, others store
// everything as a single styled <p> with markdown-in-text. Extract structural HTML at the
// tag level and fall back to text-block parsing only when a paragraph has no structure.
const parseRipplingDescription = (raw: string): DescriptionBlock[] => {
  const cleaned = raw
    .replace(/<\/?meta[^>]*>/gi, '')
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/&quot;/g, '"')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&#39;/g, "'");

  const blocks: DescriptionBlock[] = [];
  const tagRe = /<(h[1-6]|ul|ol|p)\b[^>]*>([\s\S]*?)<\/\1>/gi;

  for (const match of cleaned.matchAll(tagRe)) {
    const tag = match[1].toLowerCase();
    const inner = match[2];

    if (/^h[1-6]$/.test(tag)) {
      const text = stripInline(inner);
      if (text) blocks.push({ kind: 'heading', text });
      continue;
    }

    if (tag === 'ul' || tag === 'ol') {
      const items: string[] = [];
      for (const li of inner.matchAll(/<li[^>]*>([\s\S]*?)<\/li>/gi)) {
        const t = stripInline(li[1]);
        if (t) items.push(t);
      }
      if (items.length) blocks.push({ kind: 'list', items });
      continue;
    }

    // <p>: may be a single sentence, a heading-shaped phrase, or — for the Finance-style
    // recruiter-paste case — a whole multi-section blob with <br><br> dividers and
    // markdown bullets inside.
    const text = stripInline(inner);
    if (!text) continue;

    const segments = text
      .split(/\n{2,}/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (segments.length === 1) {
      blocks.push(...segmentToBlocks(segments[0]));
    } else {
      for (const seg of segments) blocks.push(...segmentToBlocks(seg));
    }
  }

  return blocks;
};

const DescriptionBlocks = ({ blocks }: { blocks: DescriptionBlock[] }) => (
  <Col className="gap-10">
    {blocks.map((block, idx) => {
      if (block.kind === 'heading') {
        return (
          <Text key={idx} as="h3" className="text-subheader-xs text-od-content-primary">
            {block.text}
          </Text>
        );
      }
      if (block.kind === 'list') {
        return (
          <Col key={idx} className="gap-5 pl-2">
            {block.items.map((item, i) => (
              <Row key={i} className="items-start gap-4">
                <Box className="pt-0.5">
                  <Text as="span" className="text-label-lg text-od-content-secondary">
                    •
                  </Text>
                </Box>
                <Box className="shrink">
                  <Text as="span" className="text-body-lg text-od-content-primary">
                    {item}
                  </Text>
                </Box>
              </Row>
            ))}
          </Col>
        );
      }
      return (
        <Text key={idx} as="p" className="text-body-lg text-od-content-primary">
          {block.text}
        </Text>
      );
    })}
  </Col>
);

const formatPosted = (iso: string): string => {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (Number.isNaN(days)) return '';
  if (days < 1) return 'Posted today';
  if (days < 2) return 'Posted yesterday';
  if (days < 7) return `Posted ${days} days ago`;
  if (days < 30) return `Posted ${Math.floor(days / 7)} weeks ago`;
  return `Posted ${new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}`;
};

const dropUndefined = <T extends Record<string, unknown>>(obj: T): T =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as T;

const buildJobPostingSchema = (job: RipplingV2Job): object => {
  const rawLoc = job.workLocations?.[0];
  const loc = rawLoc ? locWorkplace(rawLoc) : undefined;
  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org/',
    '@type': 'JobPosting',
    title: job.name,
    description: `${job.description.company}\n${job.description.role}`,
    datePosted: job.createdOn,
    url: jobAbsoluteUrl(job),
    directApply: false,
    hiringOrganization: {
      '@type': 'Organization',
      name: job.companyName,
      sameAs: 'https://www.opendoor.com',
      logo: 'https://www.opendoor.com/icons/opendoor-logo.svg',
    },
  };
  if (job.employmentType?.id) schema.employmentType = job.employmentType.id;
  if (loc) {
    const isRemote = loc.workplaceType === 'REMOTE';
    schema.jobLocationType = isRemote ? 'TELECOMMUTE' : undefined;
    schema.jobLocation = {
      '@type': 'Place',
      address: dropUndefined({
        '@type': 'PostalAddress',
        addressLocality: loc.city,
        addressRegion: loc.state,
        addressCountry: loc.country,
      }),
    };
  }
  return dropUndefined(schema);
};

const OpenPositionCard = ({ job }: { job: ListingJob }) => (
  <Col className="justify-between gap-4 rounded-3xl border border-od-border-primary px-10 py-8 hover:border-od-border-hover-primary md:flex-row md:items-center">
    <Col className="w-full gap-2 md:w-1/2">
      <Link
        analyticsName="open-position"
        href={jobPath(job)}
        target="_blank"
        className="text-label-lg !no-underline"
      >
        {job.name}
      </Link>
      {job.board !== 'opendoor' && (
        <Text as="p" className="text-label-sm text-od-content-tertiary">
          {BOARD_LABEL[job.board]}
        </Text>
      )}
    </Col>
    <Col className="w-full md:w-1/2">
      <Text as="p" className="text-body-lg text-od-content-secondary md:text-right">
        {job.locationLabels.length === 0 ? 'Multiple locations' : job.locationLabels.join(' · ')}
      </Text>
    </Col>
  </Col>
);

const ListingView = ({ resolved }: { resolved: ListingResolved }) => {
  const { departmentFilters, officeFilters, jobsByDepartment } = resolved;
  const [selectedDepartment, setSelectedDepartment] = useState<string>();
  const [selectedOffice, setSelectedOffice] = useState<string>();

  const filteredByDepartment =
    selectedDepartment && selectedDepartment !== 'all'
      ? jobsByDepartment.filter((d) => d.label === selectedDepartment)
      : jobsByDepartment;

  const filtered =
    selectedOffice && selectedOffice !== 'all'
      ? filteredByDepartment
          .map((d) => ({
            ...d,
            jobs: d.jobs.filter((j) => j.locationLabels.includes(selectedOffice)),
          }))
          .filter((d) => d.jobs.length > 0)
      : filteredByDepartment;

  // Placeholder (not a sentinel option) is the canonical Dropdown→Combobox mapping:
  // an empty/cleared value means "show all", matching the original placeholderOption.
  // Clearing the searchable input resets to null, so the reset affordance is preserved.
  const selectedDepartmentOption =
    departmentFilters.find((option) => option.value === selectedDepartment) ?? null;
  const selectedOfficeOption =
    officeFilters.find((option) => option.value === selectedOffice) ?? null;

  return (
    <Col className="gap-16">
      <Col className="gap-8 md:flex-row">
        <Box className="w-full grow md:w-71.25 lg:grow-0">
          <Field label="Department" id="open-positions-department">
            <Combobox
              id="open-positions-department"
              options={departmentFilters}
              value={selectedDepartmentOption}
              placeholder="All Departments"
              onValueChange={(option) => setSelectedDepartment(option?.value)}
            />
          </Field>
        </Box>
        <Box className="w-full grow md:w-71.25 lg:grow-0">
          <Field label="Office" id="open-positions-office">
            <Combobox
              id="open-positions-office"
              options={officeFilters}
              value={selectedOfficeOption}
              placeholder="All Offices"
              onValueChange={(option) => setSelectedOffice(option?.value)}
            />
          </Field>
        </Box>
      </Col>

      {filtered.map((dept) => (
        <Col key={dept.label} className="gap-10">
          <Text as="h2" className="text-header-sm">
            {dept.label}
          </Text>
          <Col className="gap-4">
            {dept.jobs.map((job) => (
              <OpenPositionCard key={job.uuid} job={job} />
            ))}
          </Col>
        </Col>
      ))}
    </Col>
  );
};

const JobDetailView = ({ resolved }: { resolved: DetailResolved }) => {
  const { job } = resolved;

  if (!job) {
    return (
      <Col className="gap-4 py-25">
        <Text as="h1" className="text-header-sm">
          This role isn&rsquo;t available
        </Text>
        <Text as="p" className="text-body-lg text-od-content-secondary">
          The position may have been filled or removed.
        </Text>
        <Link
          analyticsName="back-to-listing"
          href="/careers/open-positions"
          className="text-label-lg !no-underline"
        >
          View all open positions →
        </Link>
      </Col>
    );
  }

  const allLocationNames = (job.workLocations ?? [])
    .map(locName)
    .filter((n): n is string => Boolean(n));
  let primaryLocation: string;
  if (allLocationNames.length === 0) {
    primaryLocation = 'Multiple locations';
  } else if (allLocationNames.length > 2) {
    primaryLocation = `${allLocationNames.length} locations`;
  } else {
    primaryLocation = allLocationNames.join(' · ');
  }
  const employment = job.employmentType?.id;
  const posted = formatPosted(job.createdOn);
  const applyHref = ripplingApplyUrl(job.board, job.uuid);
  const wordSet = (s: string): Set<string> =>
    new Set(
      s
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, ' ')
        .split(/\s+/)
        .filter((w) => w.length > 2),
    );
  const titleWords = wordSet(job.name);
  const stripDuplicateTitle = (blocks: DescriptionBlock[]): DescriptionBlock[] =>
    blocks.filter((b) => {
      if (b.kind !== 'heading') return true;
      const headingWords = Array.from(wordSet(b.text));
      if (headingWords.length === 0) return true;
      const overlap = headingWords.filter((w) => titleWords.has(w)).length;
      const isDupe = overlap >= 2 && overlap >= headingWords.length * 0.7;
      return !isDupe;
    });
  const roleBlocks = stripDuplicateTitle(parseRipplingDescription(job.description.role));
  const companyBlocks = stripDuplicateTitle(parseRipplingDescription(job.description.company));

  const metaItems = [primaryLocation, employment, posted].filter(Boolean) as string[];

  const eyebrowParts = [job.department.base_department.toUpperCase()];
  if (job.board !== 'opendoor') {
    eyebrowParts.push(BOARD_LABEL[job.board].toUpperCase());
  }

  return (
    <Col className="gap-16 lg:w-192">
      <Col className="gap-10">
        <Text as="p" className="text-label-sm text-od-content-tertiary">
          {eyebrowParts.join(' · ')}
        </Text>
        <Text as="h1" className="text-display-sm text-od-content-primary">
          {job.name}
        </Text>
        <Row className="flex-wrap items-center gap-4">
          {metaItems.map((item, i) => (
            <Row key={i} className="items-center gap-4">
              {i > 0 ? (
                <Text as="span" className="text-body-lg text-od-content-tertiary">
                  ·
                </Text>
              ) : null}
              <Text as="span" className="text-body-lg text-od-content-secondary">
                {item}
              </Text>
            </Row>
          ))}
        </Row>
        <Button
          analyticsName="apply-now-hero"
          aria-label="Apply now"
          size="lg"
          variant="primary"
          href={applyHref}
          className="self-start"
        >
          Apply now
        </Button>
      </Col>

      <Box className="h-px w-full bg-od-border-primary" />

      <DescriptionBlocks blocks={roleBlocks} />

      <Col className="pt-4">
        <DescriptionBlocks blocks={companyBlocks} />
      </Col>

      <Col className="items-start gap-8 rounded-2xl bg-od-bg-secondary p-16">
        <Text as="h3" className="text-subheader-md text-od-content-primary">
          Ready to apply?
        </Text>
        <Text as="p" className="text-body-lg text-od-content-secondary">
          You&rsquo;ll be redirected to our application portal to submit your resume and answer a
          few questions.
        </Text>
        <Button
          analyticsName="apply-now-footer"
          aria-label="Apply for this role"
          size="lg"
          variant="primary"
          href={applyHref}
          className="self-start"
        >
          Apply for this role
        </Button>
      </Col>
    </Col>
  );
};

const OpenPositionsCTA = (entry: ILpComponentOpenPositions, resolvedData?: Resolved) => {
  const isDetail = entry.fields.pageType === 'Job Description';

  return (
    <LandingPageSectionContainer minHeight={400} paddingBottom="$36x">
      {isDetail ? (
        <JobDetailView
          resolved={{
            job: resolvedData?.job ?? null,
            jobPostingSchema: resolvedData?.jobPostingSchema ?? null,
            seoOverride: resolvedData?.seoOverride ?? null,
          }}
        />
      ) : (
        <ListingView
          resolved={{
            departmentFilters: resolvedData?.departmentFilters ?? [],
            officeFilters: resolvedData?.officeFilters ?? [],
            jobsByDepartment: resolvedData?.jobsByDepartment ?? [],
          }}
        />
      )}
    </LandingPageSectionContainer>
  );
};

const EMPTY_LISTING: ListingResolved = {
  departmentFilters: [],
  officeFilters: [],
  jobsByDepartment: [],
};

const fetchBoardJobs = async (
  board: Board,
  signal: AbortSignal,
): Promise<Array<RipplingV1Job & { board: Board }>> => {
  const res = await fetch(ripplingListUrl(board), { signal });
  if (res.status !== 200) return [];
  const jobs = (await res.json()) as RipplingV1Job[];
  return jobs.map((j) => ({ ...j, board }));
};

const fetchListing = async (): Promise<ListingResolved> => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 5000);
  // allSettled so an OSN outage doesn't blank the Opendoor list (or vice versa).
  let jobs: Array<RipplingV1Job & { board: Board }> = [];
  try {
    const results = await Promise.allSettled(
      BOARDS.map((b) => fetchBoardJobs(b, controller.signal)),
    );
    jobs = results.flatMap((r) => (r.status === 'fulfilled' ? r.value : []));
  } catch {
    return EMPTY_LISTING;
  } finally {
    clearTimeout(timer);
  }
  if (jobs.length === 0) return EMPTY_LISTING;

  // Pass 1: collapse duplicate UUID rows (Rippling emits one row per (req, loc) for
  // multi-location reqs but reuses the UUID). Key by (board, uuid) since UUIDs are
  // only guaranteed unique within a board.
  const byKey = new Map<string, ListingJob>();
  const offices = new Set<string>();
  for (const job of jobs) {
    const loc = job.workLocation?.label;
    if (loc) offices.add(loc);
    const key = `${job.board}:${job.uuid}`;
    const existing = byKey.get(key);
    if (existing) {
      if (loc && !existing.locationLabels.includes(loc)) existing.locationLabels.push(loc);
      continue;
    }
    byKey.set(key, {
      board: job.board,
      uuid: job.uuid,
      name: job.name,
      department: job.department,
      locationLabels: loc ? [loc] : [],
    });
  }

  for (const job of byKey.values()) {
    job.locationLabels.sort((a, b) => a.localeCompare(b));
  }

  const byDept = new Map<string, ListingJob[]>();
  for (const job of byKey.values()) {
    const dept = job.department?.label ?? 'Other';
    if (!byDept.has(dept)) byDept.set(dept, []);
    byDept.get(dept)!.push(job);
  }

  const jobsByDepartment = Array.from(byDept.entries())
    .map(([label, list]) => ({
      label,
      jobs: list.sort((a, b) => a.name.localeCompare(b.name)),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const departmentFilters = jobsByDepartment.map((d) => ({ label: d.label, value: d.label }));
  const officeFilters = Array.from(offices)
    .sort((a, b) => a.localeCompare(b))
    .map((label) => ({ label, value: label }));

  return { departmentFilters, officeFilters, jobsByDepartment };
};

const fetchDetail = async ({ board, uuid }: JobRef): Promise<DetailResolved> => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 5000);
  try {
    const res = await fetch(ripplingDetailUrl(board, uuid), { signal: controller.signal });
    if (res.status !== 200) return { job: null, jobPostingSchema: null, seoOverride: null };
    const raw = (await res.json()) as Omit<RipplingV2Job, 'board'>;
    if (!raw?.uuid || !raw?.name || !raw?.description) {
      return { job: null, jobPostingSchema: null, seoOverride: null };
    }
    const job: RipplingV2Job = { ...raw, board };
    const primaryLocation = job.workLocations?.[0]
      ? locName(job.workLocations[0]) || 'Multiple locations'
      : 'Multiple locations';
    const seoOverride: SeoOverride = {
      title: `${job.name} - Opendoor`,
      canonicalUrl: jobAbsoluteUrl(job),
      metaDescription: `${job.name} at ${job.companyName} — ${primaryLocation}. Apply on opendoor.com.`,
      ogTitle: `${job.name} - Opendoor`,
      ogUrl: jobAbsoluteUrl(job),
      ogDescription: `${job.name} at ${job.companyName} — ${primaryLocation}.`,
    };
    return { job, jobPostingSchema: buildJobPostingSchema(job), seoOverride };
  } catch (_err) {
    return { job: null, jobPostingSchema: null, seoOverride: null };
  } finally {
    clearTimeout(timer);
  }
};

const openPositionsLoader = async (
  entry: ILpComponentOpenPositions,
  _root?: unknown,
  pageProps?: { paginationIndex: number; url: string },
): Promise<Resolved> => {
  if (entry.fields.pageType === 'Job Description') {
    if (!pageProps?.url) return { job: null, jobPostingSchema: null, seoOverride: null };
    const ref = extractJobRef(pageProps.url);
    if (!ref) return { job: null, jobPostingSchema: null, seoOverride: null };
    return fetchDetail(ref);
  }
  return fetchListing();
};

const OpenPositions = {
  render: OpenPositionsCTA,
  loader: openPositionsLoader,
};

export default OpenPositions;
